// A promise bounded in time: the desk's answer, or a failure that says the wait ran out — never a press
// that hangs for ever, locked, with no way to recovery (Screens' Astra review of the Pay sheet, 23 Oct 2026).
// The failure is a plain Error, which the sheet reads as an UNCERTAIN outcome: the press may have recorded.

export class TimedOut extends Error {
  constructor(ms: number) { super(`The desk did not answer within ${Math.round(ms / 1000)} seconds.`); this.name = 'TimedOut'; }
}

/** Resolve with the work's value, or reject with TimedOut after `ms`; `onTimeout` (an abort) runs then, so the work can be cut too. */
export function within<T>(ms: number, work: Promise<T>, onTimeout?: () => void): Promise<T> {
  if (!(ms > 0)) return work;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => { try { onTimeout?.(); } catch { /* the abort's own failure is not the wait's */ } reject(new TimedOut(ms)); }, ms);
  });
  return Promise.race([work, limit]).finally(() => clearTimeout(timer));
}

/** What a bounded request answers: the status and the body as text, read within the same bound as the fetch itself. */
export interface BoundedAnswer { status: number; ok: boolean; text: string; type?: ResponseType }
/**
 * fetch + the whole body under ONE bound, aborted together: a stalled body
 * after the headers is as hung as a request that never answers (Screens'
 * Astra review, 23 Oct 2026). No bound with 0.
 */
export function fetchWithin(ms: number, input: string, init: RequestInit = {}, fetchImpl: typeof fetch = fetch): Promise<BoundedAnswer> {
  const control = ms > 0 ? new AbortController() : null;
  const work = (async (): Promise<BoundedAnswer> => {
    const res = await fetchImpl(input, { ...init, ...(control ? { signal: control.signal } : {}) });
    const text = await res.text();
    return { status: res.status, ok: res.ok, text, ...(res.type ? { type: res.type } : {}) };
  })();
  return within(ms, work, () => control?.abort());
}
